import { useRef } from 'react';
import { DEVELOPMENT, ACTIVATION, type Component, type Inventory } from './api';

export function ComponentDetail({ component, inventory, onSelect, onClose }: {
  component: Component; inventory: Inventory; onSelect: (id: string) => void; onClose: () => void;
}): JSX.Element {
  const heading = useRef<HTMLHeadingElement>(null);
  const edges = inventory.edges.filter(e => e.from === component.id || e.to === component.id);
  return <section aria-label="Komponentendetail" className="mt-6 rounded-xl border border-border-strong bg-bg-surface p-4 break-words">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <h2 tabIndex={-1} ref={heading} className="text-xl font-semibold">{component.name}</h2>
      <button type="button" onClick={onClose} className="ki-control">Detail schließen</button>
    </div>
    <p className="mt-3 text-text-secondary">{component.purpose}</p>
    <dl className="mt-4 grid gap-4 sm:grid-cols-2">
      <div><dt className="text-sm text-text-tertiary">Entwicklungsstand</dt><dd>{DEVELOPMENT[component.development]}</dd></div>
      <div><dt className="text-sm text-text-tertiary">Aktivierung</dt><dd>{ACTIVATION[component.activation]}<p className="mt-1 text-sm text-text-secondary">{component.activationNote}</p></dd></div>
      <div><dt className="text-sm text-text-tertiary">Berechtigung</dt><dd>{component.permission}</dd></div>
      <div><dt className="text-sm text-text-tertiary">Beobachtete Nutzung</dt><dd>{component.observation === 'not_captured' ? 'Nicht erfasst' : 'Nicht anwendbar'}</dd></div>
    </dl>
    {component.roles && <p className="mt-4">Modellrollen: {component.roles.join(' · ')}</p>}
    {component.tool && <dl className="mt-4 space-y-2 text-sm">
      <div><dt>Operation</dt><dd className="font-mono">{component.tool.operationId}</dd></div>
      <div><dt>Verbindung / Wirkung / Datenklasse</dt><dd>{component.tool.connection === 'native' ? 'Natives Function Calling' : 'Neutraler Vertrag'} · {({ read: 'Lesen', write: 'Schreiben', client_action: 'App-Aktion' })[component.tool.effect]} · {component.tool.dataClass}</dd></div>
      <div><dt>Policy</dt><dd className="font-mono">{component.tool.policyId}</dd></div>
    </dl>}
    <h3 className="mt-5 font-semibold">Direkte Beziehungen</h3>
    {edges.length === 0 ? <p className="text-sm text-text-secondary">Keine Verbindung in diesem Profil belegt.</p> : <ul className="mt-2 space-y-3">
      {edges.map(e => {
        const related = inventory.components.find(c => c.id === (e.from === component.id ? e.to : e.from));
        return <li key={`${e.from}:${e.to}:${e.relation}`} className="text-sm">
          <span>{e.from === component.id ? 'Ausgehend' : 'Eingehend'} · {e.relation}{e.development === 'planned' ? ' · Geplant' : ''} → </span>
          <button type="button" className="ki-control text-accent-light" onClick={() => { onSelect(related!.id); heading.current?.focus(); }}>{related?.name ?? 'Unbekannte Komponente'}</button>
        </li>;
      })}
    </ul>}
    <h3 className="mt-5 font-semibold">Herkunft und Datenstand</h3>
    <ul className="mt-2 space-y-2 text-sm text-text-secondary">{component.evidence.map(e => <li key={`${e.path}:${e.symbol}`}><code>{e.path}</code> · {e.symbol}</li>)}</ul>
    <p className="mt-2 text-sm text-text-tertiary">Inventar {inventory.inventoryVersion} · {inventory.generatedAt}</p>
  </section>;
}
